// frontend/src/hooks/useFinishedThumbnails.ts
// Hook for managing finished thumbnails from the workspace (max 3 per tale)

import { useCallback, useEffect, useState } from 'react';

export interface FinishedThumbnail {
  id: string;
  slotNumber: number;
  title: string;
  subtitle?: string;
  pngUrl: string;
  svgUrl?: string;
  overlayJson?: any;
  createdAt: number;
  updatedAt: number;
}

export function useFinishedThumbnails(taleId: string) {
  const [items, setItems] = useState<FinishedThumbnail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFinishedThumbnails = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`/api/finished-thumbnails/tale/${taleId}`, { 
        credentials: 'include' 
      });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      
      setItems(data.items || []);
    } catch (e: any) {
      console.error('Failed to fetch finished thumbnails:', e);
      setError(e?.message || 'Failed to load finished thumbnails');
    } finally {
      setLoading(false);
    }
  }, [taleId]);

  useEffect(() => {
    fetchFinishedThumbnails();
  }, [fetchFinishedThumbnails]);

  const saveFinishedThumbnail = async (
    slotNumber: number,
    title: string,
    subtitle: string,
    pngData: string,
    svgData?: string,
    overlays?: any[]
  ) => {
    try {
      const res = await fetch('/api/finished-thumbnails/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          taleId,
          slotNumber,
          title,
          subtitle,
          pngData,
          svgData,
          overlays
        })
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      
      // Refresh the list
      await fetchFinishedThumbnails();
      
      return result;
    } catch (e: any) {
      console.error('Failed to save finished thumbnail:', e);
      throw e;
    }
  };

  const deleteFinishedThumbnail = async (id: string) => {
    try {
      const res = await fetch(`/api/finished-thumbnails/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      
      // Refresh the list
      await fetchFinishedThumbnails();
    } catch (e: any) {
      console.error('Failed to delete finished thumbnail:', e);
      throw e;
    }
  };

  const getAvailableSlots = () => {
    const usedSlots = items.map(item => item.slotNumber);
    return [1, 2, 3].filter(slot => !usedSlots.includes(slot));
  };

  const getSlotByNumber = (slotNumber: number) => {
    return items.find(item => item.slotNumber === slotNumber);
  };

  return {
    items,
    loading,
    error,
    refresh: fetchFinishedThumbnails,
    saveFinishedThumbnail,
    deleteFinishedThumbnail,
    getAvailableSlots,
    getSlotByNumber
  };
}
